import type { EffectiveIdentityInput, ImpersonationTarget } from './types';

/**
 * Núcleo de la impersonación. Funciones PURAS, sin React ni Supabase, para que
 * el comportamiento quede cubierto por tests en Node.
 *
 * Regla única (decisión de Eduardo, 2026-10-22): impersonar = ver EXACTAMENTE
 * lo que ve el impersonado, en solo lectura. Ya no existe la "Vista completa"
 * (ver y editar con los permisos del admin mientras se impersona):
 *   1. Sin impersonación → todo se resuelve con el perfil logueado. Ahí el
 *      Super Admin conserva su control total.
 *   2. Impersonando → rol, persona y acceso del impersonado. Si el portal no
 *      publica el rol, NO se cae al rol del admin: sin rol no se concede nada
 *      (mejor un menú de menos que enseñar lo del admin como si fuera del otro).
 *   3. Las escrituras las bloquea el guard de `solo-lectura.ts`; aquí solo se
 *      decide qué se PINTA.
 */

const SUPER_ADMIN_ROLE_NAME = 'Super Administrador';
export const SUPER_ADMIN_ROLE_ID = 1;

/** ¿Se está viendo como otro usuario? Impersonar = target publicado. */
export function isFielViewActive(target: ImpersonationTarget | null): boolean {
  return target != null;
}

/**
 * ¿Hay que consultar los permisos del rol del impersonado? Solo si el portal
 * publicó su `rolId`; sin él no hay rol que consultar (y tampoco se usa el del admin).
 */
export function usesTargetRole(target: ImpersonationTarget | null): boolean {
  return isFielViewActive(target) && typeof target?.rolId === 'number';
}

/** Rol con el que hay que consultar `submenus_permisos`. */
export function resolveEffectiveRolId(input: EffectiveIdentityInput): number | null {
  const { profileRolId, target } = input;
  if (isFielViewActive(target)) return typeof target!.rolId === 'number' ? target!.rolId : null;
  return profileRolId ?? null;
}

/** Persona efectiva: al impersonar es la del impersonado, porque los datos que
 *  se ven en pantalla son los suyos, no los del admin. */
export function resolveEffectivePersonaId(input: EffectiveIdentityInput): number | null {
  const { profilePersonaId, target } = input;
  if (isFielViewActive(target)) return target!.personaId ?? null;
  return profilePersonaId ?? null;
}

/**
 * ¿El usuario ve el portal COMPLETO, sin los recortes que aplican al usuario real?
 * Solo Super Admin y los roles con `puede_impersonar`, y solo en SU propia vista:
 * impersonando nunca, porque se ve lo que ve el otro.
 */
export function resolveFullAccess(input: EffectiveIdentityInput): boolean {
  const { profileRolNombre, puedeImpersonar, target } = input;
  if (isFielViewActive(target)) return false;
  return profileRolNombre === SUPER_ADMIN_ROLE_NAME || puedeImpersonar === true;
}

/**
 * ¿Se aplica el atajo "Super Admin ve todo" al resolver permisos? Impersonando
 * depende del impersonado (por id o por nombre de rol, según lo que publique el
 * portal), nunca del admin.
 */
export function resolveIsSuperAdminView(input: EffectiveIdentityInput): boolean {
  const { profileRolNombre, target } = input;
  if (isFielViewActive(target)) {
    if (typeof target?.rolId === 'number') return target.rolId === SUPER_ADMIN_ROLE_ID;
    return target?.rolNombre === SUPER_ADMIN_ROLE_NAME;
  }
  return profileRolNombre === SUPER_ADMIN_ROLE_NAME;
}
